import React, { useState } from 'react';
import { addOns } from '../data/packageData';
import BookingFormModal from './BookingFormModal';

const PackageModal = ({ package: selectedPackage, onClose }) => {
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [imageLoadError, setImageLoadError] = useState({});

  const handleAddOnClick = (addonId) => {
    setSelectedAddOns(prev => ({
      ...prev,
      [addonId]: (prev[addonId] || 0) + 1
    }));
  };

  const handleQuantityChange = (addonId, newQuantity) => {
    if (newQuantity < 0) return;
    setSelectedAddOns(prev => ({
      ...prev,
      [addonId]: newQuantity
    }));
    if (newQuantity === 0) {
      setSelectedAddOns(prev => {
        const newAddOns = { ...prev };
        delete newAddOns[addonId];
        return newAddOns;
      });
    }
  };

  const calculateTotal = () => {
    // Base package price
    let total = selectedPackage.price;
    
    // Add up all selected add-ons
    Object.entries(selectedAddOns).forEach(([id, quantity]) => {
      if (addOns[id] && quantity > 0) {
        total += addOns[id].price * quantity;
      }
    });
    
    return total;
  };

  // Function to prevent event bubbling
  const handleAddOnButtonClick = (e, addonId) => {
    e.stopPropagation();
    handleAddOnClick(addonId);
  };

  const handleQuantityButtonClick = (e, addonId, newQuantity) => {
    e.stopPropagation();
    handleQuantityChange(addonId, newQuantity);
  };

  const handleImageError = (index) => {
    setImageLoadError(prev => ({
      ...prev,
      [index]: true
    }));
  };

  const toggleItemImage = (index, e) => {
    e.stopPropagation();
    setExpandedItem(expandedItem === index ? null : index);
  };

  // Filter add-ons based on package type
  const getRelevantAddOns = () => {
    const isMusicPackage = selectedPackage.id === 'music';
    return Object.entries(addOns).filter(([id, addon]) => {
      // For music package, only show generator and projector
      if (isMusicPackage) {
        return ['genset-with-fuel', 'projector'].includes(id);
      }
      // For other packages, exclude music-related add-ons
      return !['genset-with-fuel', 'projector'].includes(id);
    });
  };
  
  if (!selectedPackage) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white z-10 p-6 border-b">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-3xl font-playfair font-bold text-gray-900">{selectedPackage.name}</h2>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-gray-600">{selectedPackage.description}</p>
              <span className="text-2xl font-bold text-gold-500">ZMW {selectedPackage.price}</span>
            </div>
          </div>

          <div className="p-6">
            <h3 className="text-xl font-playfair font-bold mb-4">Included Items</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {selectedPackage.items.map((item, index) => (
                <div
                  key={index}
                  className={`transition-all duration-300 ${
                    expandedItem === index ? 'bg-gray-100' : 'bg-gray-50'
                  } rounded-lg overflow-hidden cursor-pointer`}
                  onClick={(e) => toggleItemImage(index, e)}
                >
                  <div className="p-3 flex justify-between items-center">
                    <span className="text-gray-900">{item.name}</span>
                    <span className="font-medium text-gold-600">×{item.quantity}</span>
                  </div>
                  {expandedItem === index && item.image && !imageLoadError[index] && (
                    <div className="relative w-full">
                      <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-contain bg-white"
                          style={{
                            backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%, #f0f0f0), linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%, #f0f0f0)',
                            backgroundSize: '20px 20px',
                            backgroundPosition: '0 0, 10px 10px'
                          }}
                          onError={() => handleImageError(index)}
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <h3 className="text-xl font-playfair font-bold mb-4 mt-8">Available Add-ons</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {getRelevantAddOns().map(([id, addon]) => (
                <div 
                  key={id} 
                  className={`bg-gray-50 rounded-lg p-4 transition duration-200 ${
                    selectedAddOns[id] ? 'ring-2 ring-gold-500' : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 mb-1">{addon.name}</div>
                  <div className="text-gold-600 font-medium mb-2">
                    {typeof addon.price === 'number' ? `ZMW ${addon.price} each` : addon.price}
                  </div>
                  {addon.requiresInquiry ? (
                    <button
                      onClick={() => window.location.href = 'mailto:contact@example.com'}
                      className="text-sm text-gold-600 hover:text-gold-700 font-medium"
                    >
                      Contact for details
                    </button>
                  ) : selectedAddOns[id] ? (
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={(e) => handleQuantityButtonClick(e, id, selectedAddOns[id] - 1)}
                        className="bg-gray-200 hover:bg-gray-300 rounded-full w-6 h-6 flex items-center justify-center"
                      >
                        -
                      </button>
                      <span className="text-sm font-medium">{selectedAddOns[id]}</span>
                      <button 
                        onClick={(e) => handleQuantityButtonClick(e, id, selectedAddOns[id] + 1)}
                        className="bg-gray-200 hover:bg-gray-300 rounded-full w-6 h-6 flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => handleAddOnButtonClick(e, id)}
                      className="text-sm text-gold-600 hover:text-gold-700 font-medium"
                    >
                      Add to package
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Selected Add-ons Summary */}
            {Object.keys(selectedAddOns).length > 0 && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Selected Add-ons:</h4>
                {Object.entries(selectedAddOns).map(([id, quantity]) => (
                  <div key={id} className="flex justify-between items-center py-1">
                    <span>{addOns[id].name} × {quantity}</span>
                    <span className="font-medium">ZMW {addOns[id].price * quantity}</span>
                  </div>
                ))}
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex justify-between font-medium">
                    <span>Add-ons Total:</span>
                    <span>ZMW {Object.entries(selectedAddOns).reduce((sum, [id, qty]) => 
                      sum + (addOns[id].price * qty), 0
                    )}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 bg-white border-t p-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-medium">Total:</span>
              <span className="text-2xl font-bold text-gold-600">ZMW {calculateTotal()}</span>
            </div>
            <button
              onClick={() => setShowBookingForm(true)}
              className="w-full bg-gold-500 text-white py-3 px-6 rounded-lg hover:bg-gold-600 transition duration-300"
            >
              Book This Package
            </button>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <BookingFormModal
          selectedPackage={selectedPackage}
          selectedAddOns={selectedAddOns}
          onClose={(success) => {
            if (success) {
              // Close both modals after successful booking
              setShowBookingForm(false);
              setTimeout(() => {
                onClose();
              }, 500);
            } else {
              setShowBookingForm(false);
            }
          }}
        />
      )}
    </>
  );
};

export default PackageModal;
